import { useState } from 'react';

import { Button } from '../../../../../components/ui/Button';
import { Checkbox } from '../../../../../components/ui/Checkbox';
import { Dialog } from '../../../../../components/ui/Dialog';
import { Input } from '../../../../../components/ui/Input';
import { Table, type TableColumn } from '../../../../../components/ui/Table';
import { Tag } from '../../../../../components/ui/Tag';
import { Select } from '../../../../../components/ui/Select';
import type { Coupon, CouponStatus } from '../fixtures';
import { deriveCouponStatus, formatIssueTime, formatUseTime } from '../fixtures';
import { couponSourceLabel, couponStatusTone, couponTypeTagCategory } from './couponPresentation';
import styles from './CouponManagementPage.module.css';

interface CouponPickerDialogProps {
  base: readonly Coupon[];
  excludeId?: number;
  mode?: 'multiple' | 'single';
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  onSelectedChange: (ids: readonly number[]) => void;
  open: boolean;
  selected: readonly number[];
  title: string;
}

const typeOptions = ['满减券', '折扣券', '课时券'];
const statusOptions = ['进行中', '未开启', '已结束'];
const pickerTypeOptions = [{ label: '类型：不限', value: '' }, ...typeOptions.map((option) => ({ label: option, value: option }))];
const pickerStatusOptions = [{ label: '状态：不限', value: '' }, ...statusOptions.map((option) => ({ label: option, value: option }))];
const pickerSourceOptions = [
  { label: '券来源：不限', value: '' },
  { label: '优惠券', value: '优惠券' },
  { label: '电子推荐卡', value: '电子推荐卡' },
];

function statusDotClass(status: CouponStatus): string {
  const tone = couponStatusTone(status);
  return tone === 'active' ? styles.statusDotActive : tone === 'warning' ? styles.statusDotWarning : styles.statusDotMuted;
}

export function CouponPickerDialog({ base, excludeId, mode = 'multiple', onConfirm, onOpenChange, onSelectedChange, open, selected, title }: CouponPickerDialogProps) {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', source: '', status: '', type: '' });

  const visible = base.filter((coupon) => coupon.id !== excludeId
    && (!appliedFilters.search.trim() || coupon.name.includes(appliedFilters.search.trim()))
    && (!appliedFilters.type || coupon.type === appliedFilters.type)
    && (!appliedFilters.status || deriveCouponStatus(coupon) === appliedFilters.status)
    && (!appliedFilters.source || coupon.source === appliedFilters.source));

  const allSelected = visible.length > 0 && visible.every((coupon) => selected.includes(coupon.id));

  function toggle(id: number, checked: boolean) {
    if (mode === 'single') {
      onSelectedChange(checked ? [id] : []);
      return;
    }
    onSelectedChange(checked ? [...new Set([...selected, id])] : selected.filter((value) => value !== id));
  }

  function toggleAll(checked: boolean) {
    const ids = visible.map((coupon) => coupon.id);
    onSelectedChange(checked ? [...new Set([...selected, ...ids])] : selected.filter((id) => !ids.includes(id)));
  }

  function handleSourceChange(value: string) {
    setSource(value);
    if (value === '电子推荐卡') {
      setType('');
      setStatus('');
    }
  }

  function queryCoupons() {
    setAppliedFilters({ search, source, status, type });
  }

  function resetFilters() {
    setSearch('');
    setType('');
    setStatus('');
    setSource('');
    setAppliedFilters({ search: '', source: '', status: '', type: '' });
  }

  const selectedChips = selected.map((id) => {
    const coupon = base.find((item) => item.id === id);
    return coupon ? <Tag closable closeLabel={`移除${coupon.name}`} key={id} onClose={() => toggle(id, false)}>{coupon.name}</Tag> : null;
  });

  const columns: TableColumn<Coupon>[] = [
    ...(mode === 'multiple' ? [{
      fixed: 'left',
      key: 'pick',
      render: (coupon: Coupon) => <span onClick={(event) => event.stopPropagation()}><Checkbox ariaLabel={`选择${coupon.name}`} checked={selected.includes(coupon.id)} onCheckedChange={(checked) => toggle(coupon.id, checked)} value={String(coupon.id)} /></span>,
      title: <span onClick={(event) => event.stopPropagation()}><Checkbox ariaLabel="全选可用优惠券" checked={allSelected} indeterminate={!allSelected && visible.some((coupon) => selected.includes(coupon.id))} onCheckedChange={toggleAll} value="__all__" /></span>,
      width: 56,
    } satisfies TableColumn<Coupon>] : []),
    { fixed: 'left', key: 'name', title: '优惠券名称', width: 220 },
    { key: 'source', render: (coupon) => couponSourceLabel(coupon.source), title: '券来源', width: 110 },
    ...(source === '电子推荐卡' ? [] : [
      { className: styles.numeric, key: 'amount', render: (coupon: Coupon) => coupon.source === '电子推荐卡' ? null : coupon.amount, title: '面额', width: 100 },
      { key: 'type', render: (coupon: Coupon) => coupon.source === '电子推荐卡' ? null : <Tag category={couponTypeTagCategory(coupon.type)}>{coupon.type}</Tag>, title: '类型', width: 100 },
      { key: 'threshold', render: (coupon: Coupon) => coupon.source === '电子推荐卡' ? null : coupon.threshold, title: '使用门槛', width: 120 },
      { className: styles.numeric, key: 'useTime', render: (coupon: Coupon) => coupon.source === '电子推荐卡' ? null : formatUseTime(coupon.useTime), title: '用券时间', width: 180 },
      { className: styles.numeric, key: 'issueTime', render: (coupon: Coupon) => coupon.source === '电子推荐卡' ? null : formatIssueTime(coupon.issueTime), title: '发券时间', width: 140 },
      {
      key: 'status',
      render: (coupon: Coupon) => { if (coupon.source === '电子推荐卡') return null; const current = deriveCouponStatus(coupon); return <span className={styles.statusText}><span aria-hidden="true" className={statusDotClass(current)} />{current}</span>; },
      title: '状态',
      width: 100,
      } satisfies TableColumn<Coupon>,
    ]),
  ];

  return <Dialog
    footer={
      <div className={styles.pickerFooter}>
        <div className={styles.pickerSelected}>
          <strong>已选择（{selected.length}）：</strong>
          <div className={styles.pickerChips}>{selectedChips}</div>
        </div>
        <div className={styles.pickerFooterActions}>
          <Button onClick={() => onOpenChange(false)} type="default">取消</Button>
          <Button onClick={onConfirm}>确定</Button>
        </div>
      </div>
    }
    onOpenChange={onOpenChange}
    open={open}
    headerTone="brand"
    modalLayer="nested"
    size="lg"
    title={title}
  >
    <div className={styles.picker}>
      <div className={styles.pickerFilters}>
        <Input ariaLabel="搜索优惠券名称" className={styles.pickerSearch} onValueChange={setSearch} placeholder="请输入优惠券名称" value={search} />
        <Select ariaLabel="类型筛选" className={styles.pickerSelect} onValueChange={setType} options={pickerTypeOptions} value={type} />
        <Select ariaLabel="状态筛选" className={styles.pickerSelect} onValueChange={setStatus} options={pickerStatusOptions} value={status} />
        <Select ariaLabel="券来源筛选" className={styles.pickerSelect} onValueChange={handleSourceChange} options={pickerSourceOptions} value={source} />
        <Button onClick={queryCoupons}>查询</Button>
        <Button onClick={resetFilters} type="default">重置</Button>
      </div>
      <div className={styles.pickerTable}>
        <Table ariaLabel="可选优惠券" className={mode === 'single' ? styles.singlePickerTable : undefined} columns={columns} currentKey={mode === 'single' ? selected[0] : undefined} emptyText="没有符合条件的优惠券" onRowClick={(coupon) => toggle(coupon.id, mode === 'single' || !selected.includes(coupon.id))} rowKey={(coupon) => coupon.id} rows={visible} />
      </div>
    </div>
  </Dialog>;
}
